import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseHashParams, validateNonce } from '../utils/auth';
import { useDjAuth } from './DjAuthContext';
import { useSetRecoilState } from 'recoil';
import { authErrorAtom } from '../store/auth';
import { AuthError } from '../utils/auth-error';

type Props = {
  loadingElement?: React.ReactNode;
};

const DEFAULT_DJ_REDIRECT = '/';

function safelyDecodeState(state: string): string {
  try {
    return decodeURIComponent(state);
  } catch {
    return state;
  }
}

function getSafeDjRedirectPath(rawState?: string): string {
  if (!rawState) {
    return DEFAULT_DJ_REDIRECT;
  }

  const state = safelyDecodeState(rawState).trim();

  if (!state) {
    return DEFAULT_DJ_REDIRECT;
  }

  // Important: query-only navigation would keep the user on the current
  // callback route, e.g. /login/callback/dow-jones?provider=...
  if (state.startsWith('?') || state.startsWith('#')) {
    return DEFAULT_DJ_REDIRECT;
  }

  try {
    const url = new URL(state, window.location.origin);

    // prevent external redirects.
    if (url.origin !== window.location.origin) {
      return DEFAULT_DJ_REDIRECT;
    }

    // no redirect back into a login callback route.
    if (url.pathname.startsWith('/login/callback')) {
      return DEFAULT_DJ_REDIRECT;
    }

    // no redirect back to the DJ redirect starter.
    if (url.pathname === '/login-dowjones-redirect') {
      return DEFAULT_DJ_REDIRECT;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return DEFAULT_DJ_REDIRECT;
  }
}

const LoginCallbackDowJones: React.FC<Props> = ({ loadingElement }) => {
  const setAuthError = useSetRecoilState(authErrorAtom);
  const navigate = useNavigate();
  const { djAuth } = useDjAuth();

  const hasHandledCallback = useRef(false);

  useEffect(() => {
    if (hasHandledCallback.current) {
      return;
    }

    hasHandledCallback.current = true;

    const handleCallback = async () => {
      try {
        const hashParams = parseHashParams();

        const accessToken = hashParams['access_token'];
        const idToken = hashParams['id_token'];
        const refreshToken = hashParams['refresh_token'];
        const state = hashParams['state'];

        if (!accessToken || !idToken) {
          // This protects users who land on the callback route without a hash
          // after already having valid stored DJ auth.
          if (djAuth.isAuthenticated()) {
            navigate(DEFAULT_DJ_REDIRECT, { replace: true });
            return;
          }

          throw new AuthError('Missing access token or ID token');
        }

        if (!validateNonce(idToken)) {
          throw new AuthError('Nonce validation failed');
        }

        await djAuth.loginSuccess({
          id_token: idToken,
          access_token: accessToken,
          refresh_token: refreshToken || undefined,
        });

        const redirectTo = getSafeDjRedirectPath(state);

        // Remove the auth fragment from browser history before leaving callback.
        window.history.replaceState({}, document.title, window.location.pathname);

        navigate(redirectTo, { replace: true });
      } catch (err) {
        if (err instanceof AuthError) {
          setAuthError(err.userMessage);
          console.error(err.message);
        } else {
          setAuthError('An unexpected error occurred. Please try again later.');
          console.error('Unexpected error:', err);
        }

        void djAuth.logout();
      }
    };

    void handleCallback();
  }, [djAuth, navigate, setAuthError]);

  return <div className="flex flex-col justify-center items-center w-full h-screen">{loadingElement}</div>;
};

export default LoginCallbackDowJones;
